import React, { useEffect } from 'react';
import { Link, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import './NotFound.css';

/**
 * Shown for unknown URLs and for errors thrown while a route renders.
 *
 * Both cases used to fall through to react-router's built-in developer page —
 * a black-on-white stack trace with no way back to the site. A family who
 * mistypes a link or hits a broken page deserves a route onward, so this always
 * offers the three places people actually come here for.
 */
const NotFound = ({ asErrorElement = false }) => {
  // useRouteError only has a value when this renders as an errorElement.
  const error = useRouteError();
  const notFound = !asErrorElement || (isRouteErrorResponse(error) && error.status === 404);

  // Static Web Apps returns a real 404 status for unknown paths; this covers
  // the in-app navigations it never sees, so a crawler that renders the page
  // still doesn't index it. Only for "not found": a real page that errored
  // while Google rendered it (a code chunk gone after a deploy, an API
  // hiccup) must not be told to drop out of the index.
  useEffect(() => {
    if (!notFound) return undefined;
    const tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    tag.setAttribute('content', 'noindex');
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [notFound]);

  const title = notFound ? "We couldn't find that page" : 'Something went wrong';
  const body = notFound
    ? 'The link may be out of date, or the address may have a typo. These are the places people are usually looking for:'
    : 'This one is on us, not you. Reloading often clears it — and these pages are all still here:';

  return (
    <div className="nf-wrap" role="alert">
      <div className="nf-inner">
        <p className="nf-code">{notFound ? '404' : 'Error'}</p>
        <h1 className="nf-title">{title}</h1>
        <p className="nf-body">{body}</p>

        <ul className="nf-links">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/ourplaces">Our Places — find somewhere near you</Link></li>
          <li><Link to="/knowledgecenter">Knowledge Center</Link></li>
          <li><Link to="/experts">Our Experts</Link></li>
        </ul>

        {!notFound && (
          <button type="button" className="btn btn-primary nf-retry" onClick={() => window.location.reload()}>
            Reload this page
          </button>
        )}
      </div>
    </div>
  );
};

export default NotFound;
